import { MaterialType, OrderResponse, OrderToSubmitRequest } from "../../types";
import {
  BiddingWindowInput,
  argentinaDateOf,
  argentinaTimeOf,
} from "../../utils/biddingTime";
import { serviceDetailOf } from "../Order/serviceDetail";

/**
 * The window an existing order is edited in: the days and times of its opening
 * and closing instants, read in Argentina. An order from before times existed
 * comes back with its days only.
 */
export const editWindowOf = (
  order: Pick<
    OrderResponse,
    "startDate" | "finishDate" | "opensAt" | "closesAt"
  >
): Required<BiddingWindowInput> => ({
  startDateValue: order.opensAt
    ? argentinaDateOf(order.opensAt)
    : (order.startDate ?? "").slice(0, 10),
  startTimeValue: order.opensAt ? argentinaTimeOf(order.opensAt) : "",
  endDateValue: order.closesAt
    ? argentinaDateOf(order.closesAt)
    : (order.finishDate ?? "").slice(0, 10),
  endTimeValue: order.closesAt ? argentinaTimeOf(order.closesAt) : "",
});

/**
 * Convert an existing Order into the OrderToSubmitRequest shape consumed by
 * the OrderByRequest form when entering edit mode. Setting `orderId` is the
 * key signal — the submit handler dispatches to PUT /order/{id} when present.
 *
 * Per-order date / payment fields live in `generalBidData` state, not on
 * the entry — hydrate those separately in the container.
 */
export const buildEditOrderEntry = (
  order: OrderResponse
): OrderToSubmitRequest => ({
  orderId: order.id,
  id: order.material?.id,
  material: order.material,
  quantity: order.quantity,
  // The line's MATERIAL type, as the prefill seeds it, not the bidding type (NORMAL/JOINT): the step
  // that sets a service's purpose, unit and place only shows for SERVICES (SCRUM-55).
  type: order.material?.category?.type ?? MaterialType.ALL,
  spot: order.spot,
  deliveryPlace: order.deliveryPlace ?? null,
  address: order.address ?? undefined,
  purpose: order.purpose,
  brand: order.brand,
  measurementUnit: order.measurementUnit,
  coin: order.currency,
  ...serviceDetailOf(order),
  suppliers: (order.suppliers ?? []).map((s: any) => ({
    id: s.id,
    email: s.email,
    name: s.firstName,
    companyName: s.company?.name,
  })),
});
